import { List } from "@mui/material";
import { addDays, format, startOfWeek } from "date-fns";
import { useState } from "react";
import { useCases, useMe, useShifts } from "../api";
import { ShiftWeek } from "./ShiftWeek";
import { UpcomingShiftsToggle } from "./UpcomingShiftsToggle";

const parseShifts = (shifts, bubo_employee_id) => {
  const data = shifts.reduce((accumulator, shift) => {
    const {
      date,
      dayOfWeek,
      dayOfMonth,
      employeeId,
      month,
      scheduleType,
      yearWeek,
    } = shift;

    if (accumulator.hasOwnProperty(yearWeek) === false) {
      accumulator[yearWeek] = {};
    }

    if (accumulator[yearWeek].hasOwnProperty(dayOfWeek) === false) {
      accumulator[yearWeek][dayOfWeek] = { scheduleTypes: {} };
    }

    if (
      accumulator[yearWeek][dayOfWeek].scheduleTypes.hasOwnProperty(
        scheduleType,
      ) === false
    ) {
      accumulator[yearWeek][dayOfWeek].scheduleTypes[scheduleType] = {
        scheduled: [],
        unassigned: [],
      };
      accumulator[yearWeek][dayOfWeek].dayOfMonth = dayOfMonth;
      accumulator[yearWeek][dayOfWeek].month = month;
      accumulator[yearWeek][dayOfWeek].date = date;
    }

    if (employeeId === bubo_employee_id) {
      accumulator[yearWeek][dayOfWeek].scheduleTypes[scheduleType][
        "scheduled"
      ].push(shift);
    } else {
      accumulator[yearWeek][dayOfWeek].scheduleTypes[scheduleType][
        "unassigned"
      ].push(shift);
    }

    return accumulator;
  }, {});

  const now = new Date();

  return [...Array(21).keys()].reduce((accumulator, offset) => {
    const day = addDays(now, offset);
    const dayOfMonth = format(day, "d");
    const dayOfWeek = format(day, "EEE");
    const month = format(day, "MMM");
    const yearWeek = format(startOfWeek(day), "yyyyMMdd");
    const date = format(day, "yyyy-MM-dd");

    if (accumulator.hasOwnProperty(yearWeek) === false) {
      accumulator[yearWeek] = {};
    }

    if (accumulator[yearWeek].hasOwnProperty(dayOfWeek) === false) {
      accumulator[yearWeek][dayOfWeek] = { scheduleTypes: {} };
    }

    ["Swing", "Grave", "Day"].forEach((scheduleType) => {
      const scheduled =
        data?.[yearWeek]?.[dayOfWeek]?.scheduleTypes?.[scheduleType]
          ?.scheduled || [];
      const unassigned =
        data?.[yearWeek]?.[dayOfWeek]?.scheduleTypes?.[scheduleType]
          ?.unassigned || [];

      if (
        accumulator[yearWeek][dayOfWeek].scheduleTypes.hasOwnProperty(
          scheduleType,
        ) === false
      ) {
        accumulator[yearWeek][dayOfWeek].scheduleTypes[scheduleType] = {
          scheduled,
          unassigned,
        };
        accumulator[yearWeek][dayOfWeek].dayOfMonth = dayOfMonth;
        accumulator[yearWeek][dayOfWeek].month = month;
        accumulator[yearWeek][dayOfWeek].date = date;
      }
    });

    return accumulator;
  }, {});
};

const keys = ["thisWeek", "nextWeek", "weekAfterNext"];

const AvailableShifts = ({ isFilterOn }) => {
  const { data: cases } = useCases();
  const { data } = useShifts();
  const { data: me } = useMe();
  const { currentShifts, unassignedShifts } = data;
  const shifts = parseShifts([].concat(currentShifts, unassignedShifts), me.employeeId);
  const [showUpcoming, setShowUpcoming] = useState(isFilterOn);

  const hasRequestableShifts = Object.values(shifts).some((weekShifts) =>
    Object.values(weekShifts).some((dayShifts) => {
      const scheduleTypes = dayShifts?.scheduleTypes || {};

      return Object.values(scheduleTypes).some(
        ({ unassigned = [], scheduled = [] }) => {
          const isScheduled = scheduled.length > 0;
          const isAutoassignable = unassigned
            .flatMap((shift) => shift)
            .some((shift) => shift.isAutoassignable);

          return !isScheduled && isAutoassignable;
        },
      );
    }),
  );

  return (
    <div className="h-inherit w-full">
      <div className="sticky top-0 z-30 bg-white">
        <UpcomingShiftsToggle
          checked={showUpcoming}
          onChange={setShowUpcoming}
        />
      </div>

      <List sx={{ height: "inherit", width: "100%" }} subheader={<li />}>
        {showUpcoming && (!hasRequestableShifts || !me?.canAutoSchedule) ? (
          <div className="p-6 text-center text-gray-500">
            There are no shifts available to request right now
          </div>
        ) : (
          Object.keys(shifts)
            .sort()
            .map((yearWeek, index) => (
              <ShiftWeek
                key={yearWeek}
                index={index}
                scheduledHours={me.scheduledHours[keys[index]]}
                cases={cases.filter((c) => c.yearWeek === yearWeek)}
                shifts={shifts[yearWeek]}
                dolStatus={me.dolStatus}
                showUpcoming={showUpcoming}
              />
            ))
        )}
      </List>
    </div>
  );
};

export { AvailableShifts };
